// =============================================================================
// FILE: packages/renderer/src/stores/undoEngine.store.ts
// PURPOSE: R2 Advanced Undo/History Engine — "Photoshop History + AutoCAD Undo
//   + Git version control combined." Tracks 500+ actions per session with full
//   checkpoint / restore support. Provider-independent: any component can push
//   an action via useUndoEngine().push(). Persisted to localStorage keyed by
//   project so history survives page reloads.
// =============================================================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ── Types ──────────────────────────────────────────────────────────────────────
export type ActionCategory =
  | 'geometry' | 'document' | 'ai' | 'settings'
  | 'workflow' | 'report' | 'agent' | 'automation' | 'user';

export const CATEGORY_META: Record<ActionCategory, { label: string; color: string; emoji: string }> = {
  geometry:   { label: 'Geometry',   color: '#60a5fa', emoji: '📐' },
  document:   { label: 'Document',   color: '#a78bfa', emoji: '📄' },
  ai:         { label: 'AI',         color: '#25B5A8', emoji: '🤖' },
  settings:   { label: 'Settings',   color: '#94a3b8', emoji: '⚙️' },
  workflow:   { label: 'Workflow',   color: '#fbbf24', emoji: '🔄' },
  report:     { label: 'Report',     color: '#f97316', emoji: '📊' },
  agent:      { label: 'Agent',      color: '#4ade80', emoji: '🦾' },
  automation: { label: 'Automation', color: '#f43f5e', emoji: '⚡' },
  user:       { label: 'User',       color: '#e2e8f0', emoji: '👤' },
};

export interface HistoryEntry {
  id: string;
  timestamp: string; // ISO string — JSON-serializable
  label: string;
  detail?: string;
  category: ActionCategory;
  projectId?: string;
  isCheckpoint: boolean;
  checkpointLabel?: string;
  // Optional payload — components can store a before/after diff here
  before?: string;
  after?: string;
}

export interface Checkpoint {
  id: string;
  timestamp: string;
  label: string;
  historyLength: number; // snapshot of past.length at checkpoint time
}

// ── State ──────────────────────────────────────────────────────────────────────
interface UndoEngineState {
  past:        HistoryEntry[];   // oldest → newest
  future:      HistoryEntry[];   // reversed: newest undone first
  checkpoints: Checkpoint[];
  maxHistory:  number;
  locked:      boolean;          // Lock State: prevents accidental undos
  panelOpen:   boolean;

  // ── Writes ───────────────────────────────────────────────────────────────────
  push(entry: Omit<HistoryEntry, 'id' | 'timestamp' | 'isCheckpoint'>): void;
  undo(): HistoryEntry | null;
  redo(): HistoryEntry | null;
  createCheckpoint(label?: string): Checkpoint;
  restoreToCheckpoint(id: string): void;
  clearFuture(): void;
  clearHistory(): void;
  setLocked(v: boolean): void;
  openPanel(): void;
  closePanel(): void;
}

// ── Store ──────────────────────────────────────────────────────────────────────
export const useUndoEngine = create<UndoEngineState>()(
  persist(
    (set, get) => ({
      past:        [],
      future:      [],
      checkpoints: [],
      maxHistory:  500,
      locked:      false,
      panelOpen:   false,

      push(entry) {
        const { past, maxHistory } = get();
        const item: HistoryEntry = {
          ...entry,
          id:           crypto.randomUUID(),
          timestamp:    new Date().toISOString(),
          isCheckpoint: false,
        };
        set({
          past:   [...past.slice(-(maxHistory - 1)), item],
          future: [],   // any new action clears the redo stack
        });
      },

      undo() {
        const { past, future, locked } = get();
        if (locked || past.length === 0) return null;
        const entry = past[past.length - 1]!;
        set({ past: past.slice(0, -1), future: [entry, ...future] });
        return entry;
      },

      redo() {
        const { past, future, locked } = get();
        if (locked || future.length === 0) return null;
        const entry = future[0]!;
        set({ past: [...past, entry], future: future.slice(1) });
        return entry;
      },

      createCheckpoint(label?: string) {
        const { past, checkpoints } = get();
        const cp: Checkpoint = {
          id:            crypto.randomUUID(),
          timestamp:     new Date().toISOString(),
          label:         label ?? `Checkpoint at ${new Date().toLocaleTimeString()}`,
          historyLength: past.length,
        };
        // Also push a visible marker into history
        const marker: HistoryEntry = {
          id:               crypto.randomUUID(),
          timestamp:        cp.timestamp,
          label:            `★ ${cp.label}`,
          category:         'workflow',
          isCheckpoint:     true,
          checkpointLabel:  cp.label,
        };
        set({
          checkpoints: [...checkpoints, cp],
          past:        [...past, marker],
          future:      [],
        });
        return cp;
      },

      restoreToCheckpoint(id: string) {
        const { past, checkpoints } = get();
        const cp = checkpoints.find((c) => c.id === id);
        if (!cp) return;
        // Trim history back to checkpoint moment
        const trimmed = past.slice(0, cp.historyLength);
        set({ past: trimmed, future: [] });
      },

      clearFuture: () => set({ future: [] }),

      clearHistory: () => set({ past: [], future: [], checkpoints: [] }),

      setLocked: (v) => set({ locked: v }),

      openPanel:  () => set({ panelOpen: true }),
      closePanel: () => set({ panelOpen: false }),
    }),
    {
      name: 'r2-undo-engine-v1',
      // Only persist past/checkpoints — future is ephemeral
      partialize: (s) => ({
        past:        s.past.slice(-500),
        checkpoints: s.checkpoints.slice(-50),
        locked:      s.locked,
      }),
    }
  )
);

// ── Convenience hook for pushing typed actions ─────────────────────────────────
export function useAction() {
  const push = useUndoEngine((s) => s.push);
  return {
    record: (
      label: string,
      category: ActionCategory = 'user',
      opts?: { detail?: string; before?: string; after?: string; projectId?: string }
    ) => push({ label, category, ...opts }),
  };
}
